import {
  BANNER_AI_COPY,
  BANNER_AI_TITLE,
  BANNER_APPROACHING_COPY,
  BANNER_APPROACHING_COPY_FALLBACK,
  BANNER_APPROACHING_TITLE,
  BANNER_DOCS_COPY,
  BANNER_DOCS_TITLE,
  BANNER_EXCLUDED_COPY,
  BANNER_EXCLUDED_TITLE,
  BANNER_ICON_DEFAULT,
  BANNER_ICON_SUCCESS,
  BANNER_META_AI_FULFILL,
  BANNER_META_EARLIER,
  BANNER_META_LATEST,
  BANNER_SOLVED_LATEST_COPY,
  BANNER_SOLVED_OLDER_COPY,
  BANNER_SOLVED_TITLE,
  BANNER_UNATTENDED_COPY,
  BANNER_UNATTENDED_TITLE,
  BANNER_UNEXPECTED_EXIT_COPY,
  BANNER_UNEXPECTED_EXIT_TITLE,
  BANNER_WITHDRAWN_COPY,
  BANNER_WITHDRAWN_TITLE,
} from '../constants';
import type { IdvAttempt, IdvOpsResponse, IdvResolution } from '../types';
import type { IdvStateBannerView } from './types';

const sameId = (a: string | number | null | undefined, b: string | number | null | undefined): boolean =>
  a != null && b != null && String(a) === String(b);

const isWithdrawn = (status: string): boolean => status.toLowerCase().includes('withdraw');

const remainingDays = (badge: string): number | null => {
  const match = badge.match(/(\d+)\s+days?\s+remaining/i);
  return match ? Number(match[1]) : null;
};

export const formatBannerDateTime = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date} · ${time}`;
};

export const formatBannerDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const hasResolution = (resolution: IdvResolution): boolean => resolution.resolved_attempt_id != null;

const isLatestAttempt = (attempts: IdvAttempt[], attemptId: string | number): boolean =>
  sameId(attempts[0]?.id, attemptId);

const resolutionMeta = (ops: IdvOpsResponse): string | null => {
  const { resolution, attempts } = ops;
  if (!hasResolution(resolution) || !resolution.resolved_by || !resolution.resolved_at) return null;

  const resolvedId = resolution.resolved_attempt_id as string | number;
  const latest = isLatestAttempt(attempts, resolvedId);
  const resolvedAttempt = attempts.find((attempt) => sameId(attempt.id, resolvedId));
  const position = latest
    ? BANNER_META_LATEST
    : resolvedAttempt?.submitted_at
      ? `${BANNER_META_EARLIER}, ${formatBannerDate(resolvedAttempt.submitted_at)}`
      : BANNER_META_EARLIER;

  const parts = [position];
  if (resolution.resolved_kind === 'ai') parts.push(BANNER_META_AI_FULFILL);
  const agentName = resolvedAttempt?.disposition_by?.name?.trim();
  const actor = resolution.resolved_kind === 'ai' ? resolution.resolved_by : agentName || resolution.resolved_by;
  parts.push(actor, formatBannerDateTime(resolution.resolved_at));
  return parts.join(' · ');
};

const successBanner = (title: string, copy: string, ops: IdvOpsResponse): IdvStateBannerView => ({
  tone: 'success',
  title,
  copy,
  meta: resolutionMeta(ops),
  icon: BANNER_ICON_SUCCESS,
});

const banner = (
  tone: IdvStateBannerView['tone'],
  title: string,
  copy: string,
  meta: string | null = null
): IdvStateBannerView => ({
  tone,
  title,
  copy,
  meta,
  icon: tone === 'success' ? BANNER_ICON_SUCCESS : BANNER_ICON_DEFAULT,
});

export const getStateBanner = (ops: IdvOpsResponse): IdvStateBannerView | null => {
  const latest = ops.attempts[0];
  const { resolution, exclude, policy, bgc_status } = ops;

  if (hasResolution(resolution) && resolution.resolved_attempt_id != null) {
    const id = resolution.resolved_attempt_id;
    if (resolution.resolved_kind === 'ai') {
      return successBanner(BANNER_AI_TITLE, BANNER_AI_COPY(id), ops);
    }
    const copy = isLatestAttempt(ops.attempts, id) ? BANNER_SOLVED_LATEST_COPY : BANNER_SOLVED_OLDER_COPY;
    return successBanner(BANNER_SOLVED_TITLE(id), copy, ops);
  }

  if (isWithdrawn(bgc_status)) {
    return banner('neutral', BANNER_WITHDRAWN_TITLE, BANNER_WITHDRAWN_COPY);
  }

  if (exclude.excluded) {
    return banner('info', BANNER_EXCLUDED_TITLE, BANNER_EXCLUDED_COPY);
  }

  const autoWithdraw = policy.steps.find((step) => step.key === 'auto_withdraw');
  if (autoWithdraw?.state === 'next') {
    const days = remainingDays(policy.badge);
    return banner(
      'danger',
      BANNER_APPROACHING_TITLE,
      days != null ? BANNER_APPROACHING_COPY(days) : BANNER_APPROACHING_COPY_FALLBACK
    );
  }

  if (latest?.disposition === 'docs_not_enough') {
    return banner('warning', BANNER_DOCS_TITLE, BANNER_DOCS_COPY(latest.id));
  }

  if (bgc_status === 'review__identity' && latest && latest.disposition == null) {
    return banner('warning', BANNER_UNATTENDED_TITLE(latest.id), BANNER_UNATTENDED_COPY);
  }

  if (bgc_status && bgc_status !== 'review__identity') {
    return banner('warning', BANNER_UNEXPECTED_EXIT_TITLE, BANNER_UNEXPECTED_EXIT_COPY);
  }

  return null;
};
